import React from "react";
import { CheckCircle2, Star, ShieldCheck, Users, Sparkles, Crown, BookOpen, Download } from "lucide-react";
import { Button } from "./Button";
import { landingContent } from "./i18n";

// Ablefy-Zahlungslinks fürs CRM (leer lassen = Button führt zur App / onLogin).
const CRM_CHECKOUT = { basic: "https://myablefy.com/s/femmesmarketingstudio/crm-studio-basic-paket-3a119a66/payment", business: "https://myablefy.com/s/femmesmarketingstudio/crm-studio-business-9f8c14d7/payment", premium: "https://myablefy.com/s/femmesmarketingstudio/crm-studio-premium-86e3a018/payment", enterprise: "https://myablefy.com/s/femmesmarketingstudio/crm-studio-white-label-beinhaltet-komplettes-crm-studio-im-premium-funktionsumfang-unter-eigener-marke-49b2a2e7/payment" };

// Sprachunabhängige Metadaten (Preis, Icon, Hervorhebung) – Texte kommen aus i18n.
const CRM_META = {
  basic: { icon: Users, price: "39", highlight: false },
  business: { icon: Sparkles, price: "59", highlight: true },
  premium: { icon: Crown, price: "119", highlight: false },
};

const CrmSection = ({ onLogin, lang = "de" }) => {
  const t = landingContent[lang].crm;
  const guideBtn = landingContent[lang].guideBtn;
  const goCrm = (url) => { if (url) window.open(url, "_blank", "noopener,noreferrer"); else if (onLogin) onLogin(); };
  const API = process.env.REACT_APP_BACKEND_URL;
  const openPdf = (file) => window.open(API ? `${API}/api/pdf/${file}` : `/pdf/${file}`, "_blank", "noopener,noreferrer");

  return (
    <section className="px-6 lg:px-12 py-16" style={{ background: "var(--brand-border-light)" }} data-testid="landing-crm">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.eyebrow}</span></div>
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.heading}</h2>
        <p className="text-center text-[var(--brand-muted)] mb-6 max-w-2xl mx-auto">{t.sub}</p>
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <Button onClick={() => openPdf("CRM_Studio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-white rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="crm-pdf-btn"><Download className="w-4 h-4 mr-2 inline-block" /> {t.pdfBtn}</Button>
          <Button onClick={() => openPdf("Anleitung_CRM_Studio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-white rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="crm-guide-btn"><BookOpen className="w-4 h-4 mr-2 inline-block" /> {guideBtn}</Button>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="crm-price-glance">
          {t.plans.map((p) => {
            const m = CRM_META[p.key];
            return (
              <div key={p.key} className={`flex items-baseline gap-2 rounded-full border px-5 py-2.5 ${m.highlight ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white" : "bg-white border-[var(--brand-border)] text-[var(--brand-ink)]"}`}>
                <span className={`text-xs uppercase tracking-wider font-semibold ${m.highlight ? "text-white/80" : "text-[var(--brand-muted)]"}`}>{p.name}</span>
                <span className="text-lg font-bold">{m.price} €</span>
                <span className={`text-xs ${m.highlight ? "text-white/70" : "text-[var(--brand-muted)]"}`}>{p.per}</span>
              </div>
            );
          })}
        </div>

        <div className="space-y-6">
          {t.plans.map((p) => {
            const m = CRM_META[p.key];
            const Icon = m.icon;
            return (
              <div key={p.key} className={`relative rounded-2xl overflow-hidden border grid grid-cols-1 md:grid-cols-5 bg-white ${m.highlight ? "border-[var(--brand-primary)] shadow-xl" : "border-[var(--brand-border)]"}`} data-testid={`crm-plan-${p.key}`}>
                {m.highlight && (<span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 bg-white text-[var(--brand-ink)] text-xs px-3 py-1 rounded-full shadow"><Star className="w-3.5 h-3.5 fill-[var(--brand-primary)] text-[var(--brand-primary)]" /> {t.recommended}</span>)}
                <div className="md:col-span-3 p-8 lg:p-10">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold mb-2"><Icon className="w-4 h-4" /> {p.name}</span>
                  <h3 className="font-[Playfair_Display] text-2xl font-bold text-[var(--brand-ink)] mb-2">{p.name}{t.packageSuffix}</h3>
                  <p className="text-sm text-[var(--brand-muted)] mb-5">{p.tagline}</p>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)] mb-3">{p.featsTitle}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {p.feats.map((ft) => (<li key={ft} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {ft}</li>))}
                  </ul>
                </div>
                <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center text-white" style={{ background: m.highlight ? "linear-gradient(160deg, #5B636C 0%, #3E454C 100%)" : "linear-gradient(160deg, #7A828B 0%, #565E66 100%)" }}>
                  <p className="text-xs uppercase tracking-wider text-white/70">{t.monthlyRent}</p>
                  <div className="my-2"><span className="text-5xl font-bold">{m.price} €</span><span className="text-white/75">{p.per}</span></div>
                  <p className="text-xs text-white/70 mb-5">{t.orYear(p.year)}</p>
                  <Button onClick={() => goCrm(CRM_CHECKOUT[p.key])} className="w-full rounded-full bg-white text-[var(--brand-ink)] hover:bg-white/90 font-semibold" data-testid={`crm-start-${p.key}`}>{CRM_CHECKOUT[p.key] ? t.buyNow : t.startNow}</Button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="flex items-start gap-2 justify-center text-center text-sm text-[var(--brand-ink)] mt-6 max-w-3xl mx-auto"><ShieldCheck className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {t.updatesNote}</p>

        <div className="mt-10 rounded-2xl border border-[var(--brand-primary)]/30 bg-[var(--brand-primary)]/5 p-6 lg:p-8 max-w-3xl mx-auto" data-testid="crm-combo-box">
          <h3 className="font-semibold text-[var(--brand-ink)] mb-2">{t.comboHeading}</h3>
          <p className="text-sm text-[var(--brand-muted)]">{t.comboText}</p>
        </div>

        <p className="text-center text-xs text-[var(--brand-muted)] mt-6 max-w-2xl mx-auto">{t.servicesNote}</p>
      </div>
    </section>
  );
};

export default CrmSection;
